import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useCompanyData } from '../hooks/useCompanyData';
import { useApi } from '../hooks/useApi';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { sanitizeText, sanitizeEmail } from '../utils/sanitize';
import { splitIva, ALICUOTAS_IVA } from '../utils/iva';
import { monedaOptionsForUser } from '../utils/multimoneda';
import { formatMoney } from '../utils/money';
import { getNextInvoiceNumber } from '../utils/invoiceNumber';
import { defaultNextRun } from '../utils/recurring';
import CurrencyFxFields from './CurrencyFxFields';
import ClienteSearchField from './ClienteSearchField';
import { FormAlert, FormSection, FormField, FormActions, formInputClass } from './form';

const CONCEPTO_VACIO = { descripcion: '', cantidad: 1, precioUnitario: 0 };

const FacturaForm = ({
  onAdd,
  initialData = null,
  editMode = false,
  onCancel,
  modoOscuro: modoOscuroProp,
  existingInvoices = null,
}) => {
  const { user } = useContext(AuthContext);
  const tc = useThemeClasses(modoOscuroProp);
  const { secureFetch } = useApi();
  const monedaOptions = useMemo(() => monedaOptionsForUser(user), [user]);
  const { loading } = useCompanyData();
  const autoNumeroRef = useRef(null);

  const [factura, setFactura] = useState({
    numero: '',
    fecha: new Date().toISOString().split('T')[0],
    cliente: '',
    conceptos: [{ ...CONCEPTO_VACIO }],
    moneda: 'ARS',
    tipoCambio: '',
    formaPago: 'Transferencia',
    vencimiento: '',
    estadoPago: 'impaga',
    recurrente: false,
    proximaFecha: '',
    notas: '',
    tipo: 'C',
    alicuotaIva: 0,
    subtotal: 0,
    tax: 0,
    email: '',
    pais: '',
    razonSocial: '',
    total: 0,
    documentType: 'invoice',
    categoria: ''
  });

  const [clientes, setClientes] = useState([]);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const brutoConceptos = factura.conceptos.reduce((sum, c) => sum + (c.cantidad * c.precioUnitario), 0);
    const split = splitIva({
      importe: brutoConceptos,
      alicuota: factura.tipo === 'C' ? 0 : factura.alicuotaIva,
      tipo: factura.tipo
    });
    if (
      split.total !== factura.total ||
      split.neto !== factura.subtotal ||
      split.iva !== factura.tax
    ) {
      setFactura((prev) => ({
        ...prev,
        subtotal: split.neto,
        tax: split.iva,
        total: split.total,
        alicuotaIva: split.alicuota
      }));
    }
  }, [factura.conceptos, factura.tipo, factura.alicuotaIva, factura.total, factura.subtotal, factura.tax]);

  useEffect(() => {
    if (!initialData) return;
    const adaptedData = { ...initialData };

    if (initialData.conceptos?.length > 0) {
      adaptedData.conceptos = initialData.conceptos.map((c) => ({
        descripcion: c.detalle || c.descripcion || '',
        cantidad: c.cantidad || 1,
        precioUnitario: c.precioUnitario || (c.importe / (c.cantidad || 1)) || 0
      }));
    } else if (adaptedData.concepto || adaptedData.cantidad || adaptedData.precioUnitario) {
      adaptedData.conceptos = [{
        descripcion: adaptedData.concepto || '',
        cantidad: adaptedData.cantidad || 1,
        precioUnitario: adaptedData.precioUnitario || 0
      }];
    }
    if (!adaptedData.conceptos?.length) {
      adaptedData.conceptos = [{ ...CONCEPTO_VACIO }];
    }
    if (!adaptedData.cliente && adaptedData.razonSocial) {
      adaptedData.cliente = adaptedData.razonSocial;
    }
    setFactura(adaptedData);
  }, [initialData]);

  const fetchClientes = React.useCallback(async () => {
    try {
      const res = await secureFetch('/api/clients');
      if (res.ok) setClientes(await res.json());
    } catch {
      // silencioso
    }
  }, [secureFetch]);

  useEffect(() => { fetchClientes(); }, [fetchClientes]);

  useEffect(() => {
    if (editMode) return undefined;
    let cancelled = false;

    const applyNextNumero = (invoices) => {
      if (cancelled) return;
      const next = getNextInvoiceNumber(invoices);
      setFactura((prev) => {
        if (prev.numero && prev.numero !== autoNumeroRef.current) return prev;
        autoNumeroRef.current = next;
        return { ...prev, numero: next };
      });
    };

    if (Array.isArray(existingInvoices)) {
      applyNextNumero(existingInvoices);
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const res = await secureFetch('/api/invoices');
        if (!res.ok || cancelled) return;
        applyNextNumero(await res.json());
      } catch {
        // silencioso
      }
    })();

    return () => { cancelled = true; };
  }, [editMode, initialData, existingInvoices, secureFetch]);

  useEffect(() => {
    if (editMode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await secureFetch('/api/fiscal/perfil');
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data.perfil?.regimenFiscal === 'ri') {
          setFactura((prev) => {
            if (prev.tipo !== 'C' || prev.alicuotaIva) return prev;
            return { ...prev, tipo: 'A', alicuotaIva: 21 };
          });
        }
      } catch {
        // silencioso
      }
    })();
    return () => { cancelled = true; };
  }, [editMode, initialData, secureFetch]);

  const seleccionarCliente = (cliente) => {
    setFactura((prev) => ({
      ...prev,
      cliente: cliente.razonSocial || cliente.name || '',
      email: cliente.email || '',
      pais: cliente.pais || 'Argentina',
      razonSocial: cliente.razonSocial || cliente.name || '',
    }));
  };

  const handleChange = (field, value) => {
    let sanitizedValue = value;
    switch (field) {
      case 'email':
        sanitizedValue = sanitizeEmail(value);
        break;
      case 'numero':
        sanitizedValue = value.replace(/[^a-zA-Z0-9-]/g, '');
        break;
      case 'notas':
        sanitizedValue = sanitizeText(value);
        break;
      default:
        if (typeof value === 'string') sanitizedValue = sanitizeText(value);
    }

    setFactura((prev) => {
      if (field === 'tipo') {
        const tipo = value;
        const alicuotaIva = tipo === 'C' ? 0 : (Number(prev.alicuotaIva) || 21);
        return { ...prev, tipo, alicuotaIva };
      }
      if (field === 'alicuotaIva') {
        return { ...prev, alicuotaIva: parseFloat(value) || 0 };
      }
      return { ...prev, [field]: sanitizedValue };
    });
  };

  const updateConcepto = (idx, field, value) => {
    setFactura((prev) => {
      const conceptos = prev.conceptos.map((c, i) =>
        i === idx ? { ...c, [field]: value } : c
      );
      return { ...prev, conceptos };
    });
  };

  const agregarConcepto = () => {
    setFactura((prev) => ({
      ...prev,
      conceptos: [...prev.conceptos, { ...CONCEPTO_VACIO }]
    }));
  };

  const quitarConcepto = (idx) => {
    setFactura((prev) => {
      if (prev.conceptos.length <= 1) return prev;
      return { ...prev, conceptos: prev.conceptos.filter((_, i) => i !== idx) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');

    if (!factura.cliente.trim()) {
      setError('Ingresá el cliente antes de guardar.');
      return;
    }
    if (!factura.conceptos.some((c) => c.descripcion.trim() && c.precioUnitario > 0)) {
      setError('Agregá al menos un concepto con descripción e importe.');
      return;
    }
    if (typeof onAdd !== 'function') {
      setError('No se pudo procesar el registro. Reintentá o actualizá la página.');
      return;
    }

    const brutoConceptos = factura.conceptos.reduce((sum, c) => sum + (c.cantidad * c.precioUnitario), 0);
    const split = splitIva({
      importe: brutoConceptos,
      alicuota: factura.tipo === 'C' ? 0 : factura.alicuotaIva,
      tipo: factura.tipo
    });

    const tcNum = Number(factura.tipoCambio) || null;
    setSubmitting(true);
    try {
      await onAdd({
        ...factura,
        numero: sanitizeText(factura.numero),
        cliente: sanitizeText(factura.cliente),
        email: sanitizeEmail(factura.email),
        pais: sanitizeText(factura.pais),
        razonSocial: sanitizeText(factura.razonSocial || factura.cliente),
        notas: sanitizeText(factura.notas),
        categoria: sanitizeText(factura.categoria),
        subtotal: split.neto,
        tax: split.iva,
        total: split.total,
        tipoCambio: factura.moneda === 'ARS' ? null : tcNum,
        fuenteCotizacion: factura.moneda === 'ARS' ? null : (factura.fuenteCotizacion || undefined),
        tipo: factura.tipo || 'C',
        alicuotaIva: split.alicuota,
        conceptos: factura.conceptos.map((c) => ({
          detalle: sanitizeText(c.descripcion),
          importe: c.cantidad * c.precioUnitario
        }))
      });
    } catch (submitError) {
      setError(submitError?.message || 'No se pudo guardar el registro.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1F80FF]" />
      </div>
    );
  }

  const input = formInputClass(tc.inputClass);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <FormAlert type="error">{error}</FormAlert>

      <FormSection
        title="Comprobante"
        description="Número, fecha y estado del registro."
        section={tc.section}
        textPri={tc.textPri}
        textMuted={tc.textMuted}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FormField
              label="Número"
              required
              labelClass={tc.labelClass}
              textMuted={tc.textMuted}
              hint={!editMode ? 'Se sugiere el siguiente número según tus comprobantes.' : undefined}
            >
              <input
                type="text"
                value={factura.numero}
                onChange={(e) => handleChange('numero', e.target.value)}
                required
                className={input}
                placeholder="Ej. 0001-00001234"
              />
            </FormField>
            <FormField label="Fecha" required labelClass={tc.labelClass}>
              <input
                type="date"
                value={factura.fecha}
                onChange={(e) => handleChange('fecha', e.target.value)}
                required
                className={input}
              />
            </FormField>
            <FormField label="Estado" labelClass={tc.labelClass}>
              <select
                value={factura.estadoPago}
                onChange={(e) => handleChange('estadoPago', e.target.value)}
                className={input}
              >
                <option value="impaga">Impaga</option>
                <option value="pagada">Pagada</option>
              </select>
            </FormField>
            <FormField label="Categoría" labelClass={tc.labelClass}>
              <select
                value={factura.categoria || ''}
                onChange={(e) => handleChange('categoria', e.target.value)}
                className={input}
              >
                <option value="">Sin categoría</option>
                <option value="Honorarios">Honorarios</option>
                <option value="Asesoría">Asesoría</option>
                <option value="Proyecto">Proyecto</option>
                <option value="Otro">Otro</option>
              </select>
            </FormField>
          </div>
          {!editMode && (
            <FormField label="Repetir" labelClass={tc.labelClass}>
              <label className={`flex items-center gap-2 text-sm ${tc.textPri}`}>
                <input
                  type="checkbox"
                  checked={!!factura.recurrente}
                  onChange={(e) => handleChange('recurrente', e.target.checked)}
                />
                Repetir cada mes
              </label>
              {factura.recurrente && (
                <input
                  type="date"
                  value={factura.proximaFecha || defaultNextRun(factura.fecha)}
                  onChange={(e) => handleChange('proximaFecha', e.target.value)}
                  className={`${input} mt-2 max-w-xs`}
                />
              )}
            </FormField>
          )}
        </div>
      </FormSection>

      <FormSection
        title="Cliente"
        description="Buscá un cliente existente o escribí uno nuevo."
        section={tc.section}
        textPri={tc.textPri}
        textMuted={tc.textMuted}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="" labelClass={tc.labelClass} className="md:col-span-2">
            <ClienteSearchField
              id="factura-cliente-search"
              value={factura.cliente}
              onChange={(valor) => setFactura((prev) => ({ ...prev, cliente: valor }))}
              onSelect={seleccionarCliente}
              clientes={clientes}
              inputClass={input}
              boxClass={tc.box}
              divideClass={tc.divide}
              textPri={tc.textPri}
              textMuted={tc.textMuted}
              labelClass={tc.labelClass}
              label="Nombre / Razón social"
              required
              placeholder="Buscar o escribir cliente…"
            />
          </FormField>
          <FormField label="Email" labelClass={tc.labelClass}>
            <input
              type="email"
              value={factura.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={input}
              placeholder="correo@cliente.com"
            />
          </FormField>
          <FormField label="País" labelClass={tc.labelClass}>
            <input
              type="text"
              value={factura.pais}
              onChange={(e) => handleChange('pais', e.target.value)}
              className={input}
              placeholder="Argentina"
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection
        title="Conceptos"
        description="Detalle de lo facturado. Podés agregar varias líneas."
        section={tc.section}
        textPri={tc.textPri}
        textMuted={tc.textMuted}
      >
        <div className="space-y-3">
          <div className={`hidden sm:grid grid-cols-12 gap-2 text-[11px] font-medium uppercase tracking-wide ${tc.textMuted}`}>
            <div className="col-span-5">Descripción</div>
            <div className="col-span-2">Cant.</div>
            <div className="col-span-3">Precio unit.</div>
            <div className="col-span-2 text-right">Subtotal</div>
          </div>
          {factura.conceptos.map((concepto, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
              <div className="sm:col-span-5">
                <input
                  type="text"
                  value={concepto.descripcion}
                  onChange={(e) => updateConcepto(idx, 'descripcion', e.target.value)}
                  required
                  className={input}
                  placeholder="Descripción del servicio o producto"
                />
              </div>
              <div className="sm:col-span-2">
                <input
                  type="number"
                  value={concepto.cantidad}
                  onChange={(e) => updateConcepto(idx, 'cantidad', parseInt(e.target.value, 10) || 1)}
                  min="1"
                  required
                  className={input}
                  placeholder="Cant."
                />
              </div>
              <div className="sm:col-span-3">
                <input
                  type="number"
                  value={concepto.precioUnitario || ''}
                  onChange={(e) => updateConcepto(idx, 'precioUnitario', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  required
                  className={input}
                  placeholder="0,00"
                />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between sm:justify-end gap-2">
                <span className={`text-sm font-semibold ${tc.textPri}`}>
                  {(concepto.cantidad * concepto.precioUnitario).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </span>
                {factura.conceptos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => quitarConcepto(idx)}
                    className={`p-1.5 rounded-lg text-xs ${tc.btnOutline} border`}
                    title="Quitar línea"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={agregarConcepto}
            className={`text-sm font-medium text-[#1F80FF] hover:underline`}
          >
            + Agregar línea
          </button>
        </div>
      </FormSection>

      <FormSection
        title="Fiscal y totales"
        section={tc.section}
        textPri={tc.textPri}
        textMuted={tc.textMuted}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormField label="Moneda" labelClass={tc.labelClass}>
            <select
              value={factura.moneda}
              onChange={(e) => {
                const val = e.target.value;
                handleChange('moneda', val);
                if (val === 'ARS') handleChange('tipoCambio', '');
              }}
              className={input}
            >
              {monedaOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Tipo comprobante" labelClass={tc.labelClass}>
            <select
              value={factura.tipo || 'C'}
              onChange={(e) => handleChange('tipo', e.target.value)}
              className={input}
            >
              <option value="C">C — sin IVA</option>
              <option value="A">A — IVA discriminado</option>
              <option value="B">B — IVA incluido</option>
            </select>
          </FormField>
          {factura.tipo !== 'C' && (
            <FormField label="Alícuota IVA" labelClass={tc.labelClass}>
              <select
                value={factura.alicuotaIva || 21}
                onChange={(e) => handleChange('alicuotaIva', e.target.value)}
                className={input}
              >
                {ALICUOTAS_IVA.filter((a) => a > 0).map((a) => (
                  <option key={a} value={a}>{String(a).replace('.', ',')}%</option>
                ))}
              </select>
            </FormField>
          )}
          <FormField label="Total" labelClass={tc.labelClass}>
            <div className={`px-3 py-2.5 rounded-xl border text-sm font-bold ${tc.readonlyBox}`}>
              {formatMoney(factura.total, factura.moneda, { showCode: true })}
            </div>
            {factura.tax > 0 && (
              <p className={`text-[11px] mt-1 ${tc.textMuted}`}>
                Neto {factura.subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                {' · '}IVA {factura.tax.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                {factura.tipo === 'A' ? ' · precios sin IVA' : ' · precios con IVA'}
              </p>
            )}
          </FormField>
        </div>

        <CurrencyFxFields
          moneda={factura.moneda}
          monto={factura.total}
          tipoCambio={factura.tipoCambio}
          onTipoCambioChange={(val) => handleChange('tipoCambio', val)}
          labelClass={tc.labelClass}
          textMuted={tc.textMuted}
          tc={tc}
        />
      </FormSection>

      {showAdvancedFields && (
        <FormSection
          title="Opciones adicionales"
          section={tc.section}
          textPri={tc.textPri}
          textMuted={tc.textMuted}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Forma de pago" labelClass={tc.labelClass}>
              <select
                value={factura.formaPago}
                onChange={(e) => handleChange('formaPago', e.target.value)}
                className={input}
              >
                <option value="Transferencia">Transferencia</option>
                <option value="Efectivo">Efectivo</option>
                <option value="Cheque">Cheque</option>
                <option value="Tarjeta">Tarjeta</option>
              </select>
            </FormField>
            <FormField label="Vencimiento" labelClass={tc.labelClass}>
              <input
                type="date"
                value={factura.vencimiento}
                onChange={(e) => handleChange('vencimiento', e.target.value)}
                className={input}
              />
            </FormField>
            <FormField label="Notas" labelClass={tc.labelClass} className="sm:col-span-2">
              <textarea
                value={factura.notas}
                onChange={(e) => handleChange('notas', e.target.value)}
                rows={2}
                className={input}
                placeholder="Observaciones internas…"
              />
            </FormField>
          </div>
        </FormSection>
      )}

      <button
        type="button"
        onClick={() => setShowAdvancedFields((v) => !v)}
        className={`text-xs ${tc.textMuted} hover:underline`}
      >
        {showAdvancedFields ? 'Ocultar opciones avanzadas ↑' : 'Mostrar opciones avanzadas ↓'}
      </button>

      <FormActions
        submitLabel={editMode ? 'Actualizar registro' : 'Registrar venta'}
        loading={submitting}
        onCancel={onCancel}
        showCancel={Boolean(onCancel)}
        btnPrimary={tc.btnPrimary}
        btnOutline={tc.btnOutline}
      />
    </form>
  );
};

export default FacturaForm;
